import { useRef, useState, type ReactNode } from 'react'
import './chartInteraction.css'

// Shared chart-interaction primitives for the CFO chart kit. Charts stay
// presentation-first; this adds two opt-in behaviors without making each SVG
// own AI logic:
//   • a cursor-following value tooltip (hover a data point → see its value), and
//   • a typed selection callback so a click can resolve to a specific element
//     (e.g. the "Cost" bar) or to the chart in general.
//
// Time-series charts use the tooltip for hover but resolve clicks to
// `{ kind: 'chart' }` (no point selection); categorical charts (bars/waterfall)
// resolve a clicked element to `{ kind: 'element', ... }`.

export type CfoChartSelection =
  | { kind: 'chart' }
  | { kind: 'element'; label: string; value: number | null; index: number }

export type CfoChartTip = { x: number; y: number; label: string; value: string } | null

/** Tooltip state + a positioned container ref. Position is relative to the frame. */
export function useChartTooltip() {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [tip, setTip] = useState<CfoChartTip>(null)
  const show = (clientX: number, clientY: number, value: string, label = '') => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return
    setTip({ x: clientX - rect.left, y: clientY - rect.top, label, value })
  }
  const hide = () => setTip(null)
  return { containerRef, tip, show, hide }
}

export function CfoChartTooltip({ tip }: { tip: CfoChartTip }) {
  if (!tip) return null
  // Clamp left so the bubble never spills past the frame's left edge.
  const left = Math.max(8, tip.x)
  return (
    <div className="cfo-chart-tip" style={{ left, top: tip.y }} aria-hidden="true">
      <b>{tip.value}</b>
      {tip.label ? <span>{tip.label}</span> : null}
    </div>
  )
}

/**
 * Positioned frame that hosts an interactive SVG + its tooltip. Renders a plain
 * div (no button role) so child elements can own their own clicks/keyboard; the
 * AI-affordance ring/spark is supplied by the surrounding CfoInteractiveChart in
 * `interactiveChildren` mode.
 */
export function CfoChartFrame({
  containerRef,
  tip,
  className,
  children,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>
  tip: CfoChartTip
  className?: string
  children: ReactNode
}) {
  return (
    <div className={`cfo-chart-frame${className ? ` ${className}` : ''}`} ref={containerRef}>
      {children}
      <CfoChartTooltip tip={tip} />
    </div>
  )
}
